import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, Printer, ArrowsClockwise, FileText, PushPin, PushPinSlash, FileXls } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { ReportChart } from "./ReportChart";
import { fmtNumber, fmtDate, currentLang } from "../lib/i18n";
import { ReportAction } from "./ReportAction";

const num = (v, digits = 2) => fmtNumber(v, { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtCell = (v, type) => {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "money") return num(v, 2);
  if (type === "num") return num(v, 1);
  if (type === "int") return fmtNumber(v);
  if (type === "date") return fmtDate(v);
  return String(v);
};
const RIGHT = new Set(["money", "num", "int"]);
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

export function StandardReports({ module }) {
  const [catalog, setCatalog] = useState([]);
  const [active, setActive] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [pinned, setPinned] = useState([]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const wanted = qs.get("report");
    Promise.all([api.get("/reports/catalog", { params: { module } }), api.get("/reports/favourites")]).then(([c, f]) => {
      setCatalog(c.data); setPinned(f.data.report_ids);
      const first = (wanted && c.data.find((r) => r.id === wanted)?.id) || c.data.find((r) => f.data.report_ids.includes(r.id))?.id || c.data[0]?.id || null;
      const period = qs.get("period") || (first && f.data.periods?.[first]);
      if (period && period !== "all" && c.data.find((r) => r.id === first)?.dated) {
        const to = today(); const d = new Date();
        if (period === "ytd") setFrom(firstOfYear()); else { d.setDate(d.getDate() - (period === "90d" ? 90 : 30)); setFrom(d.toISOString().slice(0, 10)); }
        setTo(to);
      }
      setActive((a) => a || first);
    }).catch(() => {});
  }, [module]);

  const togglePin = async (e, rid) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/reports/favourites/${rid}/toggle`);
      setPinned(data.report_ids);
      toast.success(data.pinned ? "Pinned — it now shows first here and on your Dashboard" : "Unpinned");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const ordered = useMemo(() => [...catalog].sort((a, b) => Number(pinned.includes(b.id)) - Number(pinned.includes(a.id))), [catalog, pinned]);
  const pinnedCount = ordered.filter((c) => pinned.includes(c.id)).length;

  const run = useCallback(async (rid = active, f = from, t = to) => {
    if (!rid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${rid}`, { params: { ...(f ? { from: f } : {}), ...(t ? { to: t } : {}) } });
      setData(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [active, from, to]);

  // Re-run only when the selected report changes; date edits are applied via RUN / presets.
  useEffect(() => { run(active, from, to); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (ext = "csv") => {
    try {
      const mime = ext === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv;charset=utf-8;";
      const { data: blob } = await api.get(`/reports/${active}.${ext}`, { params: { ...(from ? { from } : {}), ...(to ? { to } : {}), lang: currentLang() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([blob], { type: mime }));
      const a = document.createElement("a"); a.href = url; a.download = `${active}-${today()}.${ext}`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.rows;
    const needle = q.toLowerCase();
    return data.rows.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(needle)));
  }, [data, q]);

  const meta = useMemo(() => catalog.find((c) => c.id === active), [catalog, active]);
  const keyCol = data?.columns[0]?.key;
  const extraTotals = useMemo(() => (data ? Object.entries(data.totals || {}).filter(([k]) => !data.columns.some((c) => c.key === k)) : []), [data]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid={`reports-${module}`}>
      <aside className="border-b lg:border-b-0 lg:border-r border-zinc-950 bg-zinc-50/60 print:hidden">
        <div className="px-4 py-3 label-sm text-zinc-500 border-b border-zinc-200">STANDARD REPORTS · {catalog.length}</div>
        <ul data-testid="reports-list">
          {ordered.map((c, i) => {
            const isPinned = pinned.includes(c.id);
            return (
              <li key={c.id}>
                {i === 0 && pinnedCount > 0 && <div className="px-4 pt-2 pb-1 text-[9px] font-mono tracking-widest text-[#FF3E00]" data-testid="reports-pinned-divider">PINNED</div>}
                {i === pinnedCount && pinnedCount > 0 && <div className="px-4 pt-2 pb-1 text-[9px] font-mono tracking-widest text-zinc-400">ALL REPORTS</div>}
                <div role="button" tabIndex={0} onClick={() => setActive(c.id)} onKeyDown={(e) => e.key === "Enter" && setActive(c.id)} data-testid={`report-item-${c.id}`}
                  className={`w-full text-left px-4 py-2.5 border-b border-zinc-100 transition-colors cursor-pointer flex items-start gap-2 ${active === c.id ? "bg-zinc-950 text-white" : "hover:bg-white"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold flex items-center gap-2"><FileText size={12} /> {c.name}</div>
                    <div className={`text-[10px] mt-0.5 ${active === c.id ? "text-zinc-300" : "text-zinc-500"}`}>{c.description}</div>
                  </div>
                  <button onClick={(e) => togglePin(e, c.id)} title={isPinned ? "Unpin" : "Pin to top + Dashboard"} data-testid={`report-pin-${c.id}`}
                    className={`shrink-0 mt-0.5 ${isPinned ? "text-[#FF3E00]" : active === c.id ? "text-zinc-400 hover:text-white" : "text-zinc-300 hover:text-zinc-950"}`}>
                    {isPinned ? <PushPin size={12} weight="fill" /> : <PushPinSlash size={12} />}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      <section className="min-w-0">
        <div className="px-5 py-3 border-b border-zinc-950 flex flex-wrap items-end gap-3 print:hidden" data-testid="report-toolbar">
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">From{meta?.dated ? ` · ${meta.dated}` : ""}</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} disabled={!meta?.dated} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none tnum disabled:opacity-40" data-testid="report-from" />
          </label>
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} disabled={!meta?.dated} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none tnum disabled:opacity-40" data-testid="report-to" />
          </label>
          {meta?.dated && (
            <div className="flex gap-1 text-[10px] font-mono">
              <button onClick={() => { setFrom(firstOfYear()); setTo(today()); run(active, firstOfYear(), today()); }} className="px-2 py-1 border border-zinc-300 hover:border-zinc-950" data-testid="report-preset-ytd">YTD</button>
              <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); const f = d.toISOString().slice(0, 10); setFrom(f); setTo(today()); run(active, f, today()); }} className="px-2 py-1 border border-zinc-300 hover:border-zinc-950" data-testid="report-preset-30d">30D</button>
              <button onClick={() => { setFrom(""); setTo(""); run(active, "", ""); }} className="px-2 py-1 border border-zinc-300 hover:border-zinc-950" data-testid="report-preset-all">ALL</button>
            </div>
          )}
          <button onClick={() => run()} className="bg-zinc-950 text-white text-[10px] font-semibold px-3 py-2 hover:bg-[#FF3E00] flex items-center gap-1" data-testid="report-run-btn"><ArrowsClockwise size={12} /> RUN</button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter rows…" className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none w-40" data-testid="report-filter-input" />
          <div className="ml-auto flex gap-2">
            <button onClick={() => download("xlsx")} disabled={!data} className="text-[10px] font-mono px-3 py-2 bg-zinc-950 text-white hover:bg-[#FF3E00] flex items-center gap-1 disabled:opacity-30" data-testid="report-xlsx-btn"><FileXls size={12} /> EXCEL</button>
            <button onClick={() => download("csv")} disabled={!data} className="text-[10px] font-mono px-3 py-2 border border-zinc-950 hover:bg-zinc-950 hover:text-white flex items-center gap-1 disabled:opacity-30" data-testid="report-csv-btn"><DownloadSimple size={12} /> CSV</button>
            <button onClick={() => window.print()} disabled={!data} className="text-[10px] font-mono px-3 py-2 border border-zinc-300 hover:border-zinc-950 flex items-center gap-1 disabled:opacity-30" data-testid="report-print-btn"><Printer size={12} /> PRINT</button>
          </div>
        </div>

        {data && (
          <div className="px-5 py-3 border-b border-zinc-200" data-testid="report-header">
            <div className="font-display text-xl font-black tracking-tight">{data.name}</div>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
              {data.description} · {data.from || "start"} → {data.to || "today"} · <span data-testid="report-row-count">{rows.length}{q ? ` of ${data.row_count}` : ""} rows</span> · generated {data.generated_at.slice(0, 16).replace("T", " ")} UTC by {data.generated_by}
            </div>
          </div>
        )}

        {!loading && data?.chart && rows.length > 0 && <ReportChart chart={data.chart} columns={data.columns} rows={rows} />}
        {data?.action && <ReportAction key={data.id} action={data.action} rows={rows} onDone={() => run()} />}

        <div className="overflow-x-auto">
          {loading && <div className="px-5 py-6 text-xs font-mono text-zinc-500">Running report…</div>}
          {!loading && data && (
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="bg-zinc-50 border-b border-zinc-950">
                <tr className="text-[10px] tracking-wider uppercase">
                  {data.columns.map((c) => <th key={c.key} className={`px-3 py-2 whitespace-nowrap ${RIGHT.has(c.type) ? "text-right" : "text-left"}`}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody data-testid="report-tbody">
                {rows.length === 0 && <tr><td colSpan={data.columns.length} className="px-4 py-8 text-center text-xs font-mono text-zinc-400">No data for this selection.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={`${r[keyCol] ?? ""}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    {data.columns.map((c) => <td key={c.key} className={`px-3 py-1.5 whitespace-nowrap ${RIGHT.has(c.type) ? "text-right tnum" : ""} ${c.type === "date" ? "tnum text-xs text-zinc-600" : ""}`}>{fmtCell(r[c.key], c.type)}</td>)}
                  </tr>
                ))}
              </tbody>
              {data.totals && Object.keys(data.totals).length > 0 && rows.length > 0 && (
                <tfoot className="bg-zinc-50 border-t-2 border-zinc-950 font-semibold" data-testid="report-totals">
                  <tr>
                    {data.columns.map((c, i) => (
                      <td key={c.key} className={`px-3 py-2 ${RIGHT.has(c.type) ? "text-right tnum" : ""}`}>
                        {i === 0 && !(c.key in data.totals) ? "TOTAL" : c.key in data.totals ? fmtCell(data.totals[c.key], c.type === "text" ? "int" : c.type) : ""}
                      </td>
                    ))}
                  </tr>
                  {extraTotals.length > 0 && (
                    <tr className="text-[10px] font-mono font-normal text-zinc-500">
                      <td colSpan={data.columns.length} className="px-3 py-1.5">{extraTotals.map(([k, v]) => `${k.replace(/_/g, " ")}: ${fmtCell(v, "int")}`).join(" · ")}</td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
